import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { collection, query, where, onSnapshot, limit } from 'firebase/firestore';

import { useAuth } from '../lib/auth';
import { db } from '../lib/firebase';

/**
 * How many unread notifications the badge subscription actually fetches.
 *
 * Every consumer renders the count as "9+" once it passes nine (this file and
 * BottomNav), so ten documents is all that is ever needed to draw the badge
 * correctly. This listener previously fetched EVERY unread notification —
 * a realtime subscription streaming an unbounded set to every client just to
 * render a single digit. The cap keeps it constant-cost no matter how large an
 * inbox gets, and the rendered badge is identical either way.
 */
const UNREAD_BADGE_FETCH_LIMIT = 10;

/**
 * Live unread-notification count for the active user, capped at
 * UNREAD_BADGE_FETCH_LIMIT. Extracted from NotificationBell so other surfaces
 * (e.g. the desktop sidebar Notifications entry — IMP-021) can show the same
 * unread signal without duplicating the Firestore subscription logic. Returns 0
 * when signed out. Callers must treat the value as "at least this many" and
 * render anything above nine as "9+", which they already do.
 */
export function useUnreadNotificationCount(): number {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    // Listen only to unread notifications for the active user
    const q = query(
      collection(db, 'users', user.uid, 'notifications'),
      where('read', '==', false),
      limit(UNREAD_BADGE_FETCH_LIMIT)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setUnreadCount(snap.size);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[NotificationBell] Subscription failed:', err);
      }
    );

    return () => unsub();
  }, [user]);

  return unreadCount;
}

export function NotificationBell() {
  const unreadCount = useUnreadNotificationCount();
  const navigate = useNavigate();

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes bellWiggle {
          0%, 100% { transform: rotate(0); }
          15% { transform: rotate(15deg); }
          30% { transform: rotate(-12deg); }
          45% { transform: rotate(10deg); }
          60% { transform: rotate(-8deg); }
          75% { transform: rotate(4deg); }
          90% { transform: rotate(-2deg); }
        }
        .hover-bell-wiggle:hover svg {
          animation: bellWiggle 0.6s ease-in-out;
          transform-origin: top center;
        }
      `}} />

      <button
        onClick={() => navigate('/notifications')}
        className="relative p-2 text-steel hover:text-orange hover:bg-orange/5 rounded-full transition-all duration-base cursor-pointer focus:outline-none hover-bell-wiggle"
        aria-label="View notifications"
      >
        <Bell size={22} className="transition-transform duration-300" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-orange text-overline font-bold text-white ring-2 ring-white animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    </>
  );
}
